import ServiceGlyph from "@/components/atoms/ServiceGlyph";
/**
 * Секция с контактной информацией (телефоны, email, адреса, IP)
 *
 * Показывает контакты с результатами валидации и развёрнутыми OSINT-карточками.
 * Дизайн адаптирован из ContactsSection:
 * - Inline-текст CallerID/мессенджеров/соцсетей/сервисов
 * - Полноценные карточки MessengerCard, SocialCard, EmailProfileCard
 * - Прогресс-бар обогащения
 * - Кликабельные аватары с модалкой
 */

import React, { useState, useEffect } from "react";
import { Loader2, X, ExternalLink, MapPin, Building } from "lucide-react";

import { cn } from "@/utils";
import type {
  PhonesValidationResult,
  EmailsValidationResult,
  ArrayValidationResult,
  ValidationFieldState,
} from "@/types/report";
import type { OsintLookup } from "@/types/osint";
import { AiAnalysisCard } from "@/components/molecules/AiAnalysisCard";
import { OsintConfidenceIcon } from "@/components/atoms";

// ============ Icon path helper ============

// Ключ сервиса для ServiceGlyph: одноцветный знак из /logo/mono.
const getServiceIconPath = (platform: string): string => platform.toLowerCase();

// ============ OSINT Config ============

const MESSENGER_CONFIG: Record<string, { logo: string; name: string }> = {
  telegram: {
    logo: getServiceIconPath("telegram"),
    name: "Telegram",
  },
  whatsapp: {
    logo: getServiceIconPath("whatsapp"),
    name: "WhatsApp",
  },
  viber: {
    logo: getServiceIconPath("viber"),
    name: "Viber",
  },
  skype: {
    logo: getServiceIconPath("skype"),
    name: "Skype",
  },
  max: { logo: getServiceIconPath("max"), name: "Max" },
};

const SOCIAL_CONFIG: Record<string, { logo: string; name: string }> = {
  vk: {
    logo: getServiceIconPath("vk"),
    name: "ВКонтакте",
  },
  ok: {
    logo: getServiceIconPath("ok"),
    name: "Одноклассники",
  },
  instagram: {
    logo: getServiceIconPath("instagram"),
    name: "Instagram",
  },
  facebook: {
    logo: getServiceIconPath("facebook"),
    name: "Facebook",
  },
  tiktok: {
    logo: getServiceIconPath("tiktok"),
    name: "TikTok",
  },
  twitter: {
    logo: getServiceIconPath("twitter"),
    name: "X (Twitter)",
  },
  linkedin: {
    logo: getServiceIconPath("linkedin"),
    name: "LinkedIn",
  },
};

const CALLER_ID_CONFIG: Record<string, { logo: string; name: string }> = {
  getcontact: { logo: getServiceIconPath("getcontact"), name: "GetContact" },
  numbuster: { logo: getServiceIconPath("numbuster"), name: "Numbuster" },
  eyecon: { logo: getServiceIconPath("eyecon"), name: "Eyecon" },
  hash: { logo: getServiceIconPath("hash"), name: "HASH" },
};

const ONLINE_SERVICES_CONFIG: Record<string, { logo: string; name: string }> = {
  apple: { logo: getServiceIconPath("apple"), name: "Apple ID" },
  gosuslugi: { logo: getServiceIconPath("gosuslugi"), name: "Госуслуги" },
  microsoft: { logo: getServiceIconPath("microsoft"), name: "Microsoft" },
  google: { logo: getServiceIconPath("google"), name: "Google" },
  spotify: { logo: getServiceIconPath("spotify"), name: "Spotify" },
  yandex: { logo: getServiceIconPath("yandex"), name: "Яндекс" },
  mailru: { logo: getServiceIconPath("mailru"), name: "Mail.ru" },
  gravatar: { logo: getServiceIconPath("gravatar"), name: "Gravatar" },
};

const EMAIL_PROFILE_CONFIG: Record<string, { logo: string; name: string }> = {
  gravatar: {
    logo: getServiceIconPath("gravatar"),
    name: "Gravatar",
  },
  github: {
    logo: getServiceIconPath("github"),
    name: "GitHub",
  },
};

// ============ Avatar Modal ============

const AvatarModal: React.FC<{ src: string; alt: string; onClose: () => void }> = ({ src, alt, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors">
        <X className="w-8 h-8" />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

// ============ Clickable Avatar ============

const ClickableAvatar: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className }) => {
  const [showModal, setShowModal] = useState(false);

  // Resolve avatar src: handle base64, URLs, etc.
  const resolvedSrc = src.startsWith("data:") ? src : src.startsWith("http") ? src : `data:image/jpeg;base64,${src}`;

  return (
    <>
      <img
        src={resolvedSrc}
        alt={alt}
        className={cn(className, "cursor-pointer hover:opacity-90 transition-opacity")}
        onClick={() => setShowModal(true)}
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = "none";
        }}
      />
      {showModal && <AvatarModal src={resolvedSrc} alt={alt} onClose={() => setShowModal(false)} />}
    </>
  );
};

// ============ OSINT Card Components ============

/* eslint-disable @typescript-eslint/no-explicit-any */

const MessengerCard: React.FC<{ module: string; data: any }> = ({ module, data }) => {
  const config = MESSENGER_CONFIG[module];
  if (!config) return null;

  const profile = data;
  const name =
    profile?.first_name && profile?.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : profile?.first_name || profile?.name || profile?.username || "";
  const username = profile?.username;
  const avatar = profile?.avatar_url || profile?.avatar || profile?.photo_url;
  const isPremium = profile?.premium;
  const isBot = profile?.bot;
  const isScam = profile?.scam;
  const isFake = profile?.fake;
  const userId = profile?.user_id;
  const profileUrl =
    profile?.profile_url || profile?.url || (module === "telegram" && username ? `https://t.me/${username}` : null);
  const bio = profile?.bio || profile?.about || profile?.description;
  const phone = profile?.phone;
  const phones = profile?.phones || [];
  const lastSeen = profile?.last_seen;
  const nameHistory = profile?.name_history || data?.name_history || profile?.names_history || [];
  const usernameHistory = profile?.username_history || profile?.all_usernames || [];
  const groups = profile?.groups || profile?.merged_groups || data?.groups || data?.merged_groups || [];

  const lastSeenLabels: Record<string, string> = {
    online: "В сети",
    recently: "Недавно",
    within_week: "На этой неделе",
    within_month: "В этом месяце",
    offline: "Не в сети",
    long_ago: "Давно",
  };

  return (
    <div className="overflow-hidden border border-line flex rounded-card">
      <div className="flex-1 p-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <ServiceGlyph service={config.logo} size={16} className="text-ink-2" title={config.name} />
              <span className="text-sm text-ink-1">{config.name}</span>
              {isPremium && (
                <span className="text-fs-2xs font-medium px-1.5 py-0.5 rounded-full bg-surface-hover text-ink-1 dark:bg-surface-hover dark:text-ink-2">
                  Premium
                </span>
              )}
              {isBot && (
                <span className="text-fs-2xs font-medium px-1.5 py-0.5 rounded-full bg-surface-hover text-ink-2">
                  Bot
                </span>
              )}
              {isScam && (
                <span className="text-fs-2xs font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  Scam
                </span>
              )}
              {isFake && (
                <span className="text-fs-2xs font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  Fake
                </span>
              )}
              {profileUrl && (
                <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-ink-3 hover:text-ink-2">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            {name && <div className="text-sm text-ink-1">{name}</div>}
            <div className="text-xs text-ink-3">
              {username && <span>@{username}</span>}
              {username && userId && <span> · </span>}
              {userId && <span>ID: {userId}</span>}
              {lastSeen && <span> · {lastSeenLabels[lastSeen] || lastSeen}</span>}
            </div>
            {(phone || phones.length > 0) && (
              <div className="text-xs mt-0.5 text-ink-3">
                {phone && <span>{phone}</span>}
                {!phone && phones.length > 0 && <span>{phones.join(", ")}</span>}
              </div>
            )}
            {bio && (
              <div className="mt-2 text-xs bg-surface-hover/50 p-2 rounded border border-line text-ink-2">{bio}</div>
            )}
          </div>
          {avatar && (
            <ClickableAvatar
              src={avatar}
              alt={name}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0 ml-3"
            />
          )}
        </div>
        {usernameHistory.length > 0 && (
          <div className="mt-3 pt-3 border-t border-line">
            <div className="text-xs mb-1 text-ink-3">История юзернеймов ({usernameHistory.length})</div>
            <div className="space-y-1">
              {usernameHistory.slice(0, 5).map((item: any, idx: number) => {
                const uname = typeof item === "string" ? item : item.username || item;
                const date = typeof item === "object" ? item.date || item.change_date : null;
                return (
                  <div key={idx} className="flex justify-between text-xs">
                    <a
                      href={`https://t.me/${uname}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ink-1 underline decoration-line-strong underline-offset-[3px] hover:decoration-ink-1 hover:underline "
                    >
                      @{uname}
                    </a>
                    {date && <span className="text-ink-3">{date}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {nameHistory.length > 0 && (
          <div className="mt-3 pt-3 border-t border-line">
            <div className="text-xs mb-1 text-ink-3">История имён ({nameHistory.length})</div>
            <div className="space-y-1">
              {nameHistory.slice(0, 5).map((item: any, idx: number) => (
                <div key={idx} className="flex justify-between text-xs">
                  <span className="text-ink-2">
                    {item.name ||
                      (item.first_name && item.last_name
                        ? `${item.first_name} ${item.last_name}`.trim()
                        : typeof item === "string"
                          ? item
                          : JSON.stringify(item))}
                  </span>
                  {item.date && <span className="text-ink-3">{item.date}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        {groups.length > 0 && (
          <div className="mt-3 pt-3 border-t border-line">
            <div className="text-xs mb-1 text-ink-3">
              {module === "telegram" ? "Telegram группы" : "Группы"} ({groups.length})
            </div>
            <div className="space-y-1">
              {groups.slice(0, 5).map((group: any, idx: number) => {
                const groupName =
                  group.name ||
                  group.title ||
                  group.chat_title ||
                  (typeof group === "string" ? group : String(group.chat_id || ""));
                const groupUsername = group.username || group.chat_username;
                const groupUrl = group.url || group.link || (groupUsername ? `https://t.me/${groupUsername}` : null);
                const membersCount = group.members_count || group.member_count;
                return (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <div>
                      {groupUrl ? (
                        <a
                          href={groupUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-ink-1 underline decoration-line-strong underline-offset-[3px] hover:decoration-ink-1 hover:underline "
                        >
                          {groupName}
                        </a>
                      ) : (
                        <span className="text-ink-2">{groupName}</span>
                      )}
                      {groupUsername && <span className="text-ink-3 ml-1">@{groupUsername}</span>}
                    </div>
                    {membersCount && (
                      <span className="text-ink-3">{Number(membersCount).toLocaleString("ru-RU")} уч.</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SocialCard: React.FC<{ module: string; data: any }> = ({ module, data }) => {
  const config = SOCIAL_CONFIG[module];
  if (!config) return null;

  const profile = data;
  const name =
    profile?.display_name ||
    profile?.name ||
    (profile?.first_name && profile?.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : profile?.first_name || "");
  const username = profile?.username;
  const avatar = profile?.avatar_url || profile?.avatar || profile?.photo_url;
  const userId = profile?.user_id || profile?.id;
  const profileUrl = profile?.profile_url || profile?.url;
  const city = profile?.city || profile?.location;
  const postsCount = profile?.posts_count ?? profile?.posts ?? profile?.videos;
  const followersCount = profile?.followers_count ?? profile?.followers;
  const followingCount = profile?.following_count ?? profile?.following;
  const likesCount = profile?.likes_count ?? profile?.likes;
  const isVerified = profile?.verified;
  const isPrivate = profile?.private;
  const bio = profile?.bio || profile?.about || profile?.status;
  const groups = profile?.groups || data?.groups || [];

  return (
    <div className="overflow-hidden border border-line flex rounded-card">
      <div className="flex-1 p-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <ServiceGlyph service={config.logo} size={16} className="text-ink-2" title={config.name} />
              <span className="text-sm text-ink-1">{config.name}</span>
              {isVerified && (
                <span className="text-fs-2xs font-medium px-1.5 py-0.5 rounded-full bg-surface-hover text-ink-1 dark:bg-surface-hover dark:text-ink-2">
                  Verified
                </span>
              )}
              {isPrivate && (
                <span className="text-fs-2xs font-medium px-1.5 py-0.5 rounded-full bg-surface-hover text-ink-2">
                  Private
                </span>
              )}
              {profileUrl && (
                <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-ink-3 hover:text-ink-2">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            {name && <div className="text-sm text-ink-1">{name}</div>}
            <div className="text-xs text-ink-3">
              {userId && <span>ID: {userId}</span>}
              {userId && username && <span> · </span>}
              {username && <span>@{username}</span>}
            </div>
            {city && (
              <div className="text-xs mt-0.5 text-ink-3">
                <MapPin className="mr-1 inline h-3.5 w-3.5 align-[-2px]" aria-hidden />
                {typeof city === "object" ? (city as any).title || (city as any).name || (city as any).city : city}
              </div>
            )}
            {(postsCount !== undefined ||
              followersCount !== undefined ||
              followingCount !== undefined ||
              likesCount !== undefined) && (
              <div className="text-xs flex flex-wrap gap-x-2 text-ink-3">
                {followersCount !== undefined && (
                  <span>{Number(followersCount).toLocaleString("ru-RU")} подписчиков</span>
                )}
                {followingCount !== undefined && <span>{Number(followingCount).toLocaleString("ru-RU")} подписок</span>}
                {postsCount !== undefined && (
                  <span>
                    {Number(postsCount).toLocaleString("ru-RU")} {module === "tiktok" ? "видео" : "постов"}
                  </span>
                )}
                {likesCount !== undefined && <span>{Number(likesCount).toLocaleString("ru-RU")} лайков</span>}
              </div>
            )}
            {bio && (
              <div className="mt-2 text-xs bg-surface-hover/50 p-2 rounded border border-line text-ink-2">{bio}</div>
            )}
          </div>
          {avatar && (
            <ClickableAvatar
              src={avatar}
              alt={name}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0 ml-3"
            />
          )}
        </div>
        {groups.length > 0 && (
          <div className="mt-3 pt-3 border-t border-line">
            <div className="text-xs mb-1 text-ink-3">Группы ({groups.length})</div>
            <div className="space-y-1">
              {groups.slice(0, 5).map((group: any, idx: number) => (
                <div key={idx} className="text-xs">
                  {group.url || group.link ? (
                    <a
                      href={group.url || group.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ink-1 underline decoration-line-strong underline-offset-[3px] hover:decoration-ink-1 hover:underline "
                    >
                      {group.name || group.title || (typeof group === "string" ? group : String(group.id || ""))}
                    </a>
                  ) : (
                    <span className="text-ink-2">
                      {group.name || group.title || (typeof group === "string" ? group : String(group.id || ""))}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const EmailProfileCard: React.FC<{ module: string; data: any }> = ({ module, data }) => {
  const config = EMAIL_PROFILE_CONFIG[module];
  if (!config) return null;

  const profile = data;
  const name =
    profile?.display_name ||
    profile?.name ||
    (profile?.first_name && profile?.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : profile?.first_name || "");
  const username = profile?.username || profile?.login;
  const avatar = profile?.avatar_url || profile?.avatar || profile?.photo_url || profile?.thumbnailUrl;
  const profileUrl = profile?.profile_url || profile?.profileUrl || profile?.html_url;
  const bio = profile?.bio || profile?.about || profile?.description;
  const publicRepos = profile?.public_repos;
  const followers = profile?.followers;
  const company = profile?.company;
  const location = profile?.location;

  return (
    <div className="overflow-hidden border border-line flex rounded-card">
      <div className="flex-1 p-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-0.5">
              <ServiceGlyph service={config.logo} size={16} className="text-ink-2" title={config.name} />
              <span className="text-sm text-ink-1">{config.name}</span>
              {profileUrl && (
                <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="text-ink-3 hover:text-ink-2">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            {name && <div className="text-sm text-ink-1">{name}</div>}
            {username && <div className="text-xs text-ink-3">@{username}</div>}
            {(publicRepos !== undefined || followers !== undefined || company || location) && (
              <div className="text-xs mt-0.5 space-y-0.5 text-ink-3">
                {(publicRepos !== undefined || followers !== undefined) && (
                  <div>
                    {publicRepos !== undefined && <span>{publicRepos} репозиториев</span>}
                    {publicRepos !== undefined && followers !== undefined && <span> · </span>}
                    {followers !== undefined && <span>{followers} подписчиков</span>}
                  </div>
                )}
                {company && (
                  <div>
                    <Building className="mr-1 inline h-3.5 w-3.5 align-[-2px]" aria-hidden />
                    {company}
                  </div>
                )}
                {location && (
                  <div>
                    <MapPin className="mr-1 inline h-3.5 w-3.5 align-[-2px]" aria-hidden />
                    {location}
                  </div>
                )}
              </div>
            )}
            {bio && (
              <div className="mt-2 text-xs bg-surface-hover/50 p-2 rounded border border-line text-ink-2">{bio}</div>
            )}
          </div>
          {avatar && (
            <ClickableAvatar
              src={avatar}
              alt={name || username || config.name}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0 ml-3"
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* eslint-enable @typescript-eslint/no-explicit-any */

// ============ Inline OSINT summary components ============

interface OsintModuleItem {
  module: string;
  found: boolean;
  result?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

/** Convert OsintLookup to flat module list for inline components */
const lookupToModules = (lookup: OsintLookup | null | undefined): OsintModuleItem[] => {
  if (!lookup) return [];
  const modules: OsintModuleItem[] = [];

  for (const [key, data] of Object.entries(lookup.caller_id_services || {})) {
    modules.push({ module: key, found: true, result: data });
  }
  for (const [key, data] of Object.entries(lookup.messengers || {})) {
    modules.push({ module: key, found: true, result: data });
  }
  for (const [key, profiles] of Object.entries(lookup.social_networks || {})) {
    if (Array.isArray(profiles) && profiles.length > 0) {
      modules.push({ module: key, found: true, result: profiles[0] });
    }
  }
  for (const [key, data] of Object.entries(lookup.online_services || {})) {
    modules.push({ module: key, found: true, result: data });
  }

  return modules;
};

const CallerIdNamesInline: React.FC<{ modules: OsintModuleItem[] }> = ({ modules }) => {
  const sources: { module: string; logo: string; name: string; names: string[]; rating?: string }[] = [];

  for (const mod of modules) {
    if (!mod.found || !mod.result) continue;
    const config = CALLER_ID_CONFIG[mod.module];
    if (!config) continue;

    const result = mod.result;
    const extractedNames: string[] = [];
    let rating: string | undefined;

    if (mod.module === "getcontact") {
      if (result?.names) extractedNames.push(...result.names);
      if (result?.name) extractedNames.unshift(result.name);
    } else if (mod.module === "numbuster") {
      if (result?.name && typeof result.name === "string") extractedNames.push(result.name);
      if (result?.names && Array.isArray(result.names)) {
        for (const n of result.names) {
          if (typeof n === "string") extractedNames.push(n);
          else if (n?.name) extractedNames.push(n.name);
        }
      }
      if (result?.rating !== undefined && result?.rating !== null) rating = parseFloat(result.rating).toFixed(1);
    } else {
      if (result?.name) extractedNames.push(result.name);
      if (result?.names) extractedNames.push(...result.names);
    }

    const uniqueNames = [...new Set(extractedNames)].filter(Boolean);
    if (uniqueNames.length > 0) {
      sources.push({ module: mod.module, logo: config.logo, name: config.name, names: uniqueNames, rating });
    }
  }

  if (sources.length === 0) return null;

  return (
    <div className="text-xs flex text-ink-3">
      <span className="flex-shrink-0">—&nbsp;</span>
      <span>
        Упоминается как:{" "}
        {sources.map((source, idx) => (
          <span key={source.module}>
            {idx > 0 && ", "}
            <ServiceGlyph
              service={source.logo}
              size={14}
              className="mr-0.5 align-text-bottom text-ink-3"
              title={source.name}
            />
            {source.name} ({source.names.join(", ")}
            {source.rating && <>, рейтинг {source.rating}</>})
          </span>
        ))}
      </span>
    </div>
  );
};

const LinkedInline: React.FC<{
  modules: OsintModuleItem[];
  configMap: Record<string, { logo: string; name: string }>;
  label: string;
}> = ({ modules, configMap, label }) => {
  const found = modules.filter((m) => m.found && configMap[m.module]);
  if (found.length === 0) return null;

  return (
    <div className="text-xs flex text-ink-3">
      <span className="flex-shrink-0">—&nbsp;</span>
      <span>
        {label}:{" "}
        {found.map((mod, idx) => {
          const config = configMap[mod.module];
          return (
            <span key={mod.module}>
              {idx > 0 && ", "}
              <ServiceGlyph
                service={config.logo}
                size={14}
                className="mr-0.5 align-text-bottom text-ink-3"
                title={config.name}
              />
              {config.name}
            </span>
          );
        })}
      </span>
    </div>
  );
};

// ============ OSINT Progress Bar ============

const OsintProgressBar: React.FC<{ completed: number; total: number }> = ({ completed, total }) => {
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="mt-2 mb-2">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-ink-3">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span className="text-xs">Загрузка OSINT</span>
        </div>
        {total > 0 && (
          <span className="text-xs text-ink-3">
            {completed} / {total} модулей
          </span>
        )}
      </div>
      <div className="w-full bg-surface-pressed rounded-full h-1">
        <div className="bg-primary-500 h-1 rounded-full transition-all duration-300" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

// ============ OSINT Sections per contact ============

const PhoneOsintSection: React.FC<{
  lookup: OsintLookup | null | undefined;
  isEnriching: boolean;
  completedModules: number;
  totalModules: number;
}> = ({ lookup, isEnriching, completedModules, totalModules }) => {
  const modules = lookupToModules(lookup);
  const messengers = modules.filter((m) => m.found && MESSENGER_CONFIG[m.module]);
  const socials = modules.filter((m) => m.found && SOCIAL_CONFIG[m.module]);
  const ai = lookup?.ai_analysis;
  const hasAi = Boolean(ai && (ai.profile_analyzer || ai.visual_analyzer));
  const hasAnyData = modules.length > 0 || hasAi;

  if (!isEnriching && !hasAnyData) return null;
  if (isEnriching && !hasAnyData) return <OsintProgressBar completed={completedModules} total={totalModules} />;

  return (
    <div className="space-y-0.5 mt-1">
      {isEnriching && completedModules < totalModules && (
        <OsintProgressBar completed={completedModules} total={totalModules} />
      )}

      {hasAi && (
        <div className="mt-1.5 mb-2">
          <AiAnalysisCard data={ai} />
        </div>
      )}

      <CallerIdNamesInline modules={modules} />
      <LinkedInline modules={modules} configMap={MESSENGER_CONFIG} label="Привязанные мессенджеры" />
      {messengers.length > 0 && (
        <div className="space-y-2 mt-2 mb-2">
          {messengers.flatMap((mod) => {
            const profiles = mod.result?.profiles || [mod.result];
            return profiles.map((profile: any, idx: number) => (
              <MessengerCard key={`${mod.module}-${idx}`} module={mod.module} data={profile} />
            ));
          })}
        </div>
      )}

      <LinkedInline modules={modules} configMap={SOCIAL_CONFIG} label="Найден в социальных сетях" />
      {socials.length > 0 && (
        <div className="space-y-2 mt-2 mb-2">
          {socials.flatMap((mod) => {
            const allProfiles = lookup?.social_networks?.[mod.module] || [mod.result];
            return allProfiles.map((profile: any, idx: number) => (
              <SocialCard key={`${mod.module}-${idx}`} module={mod.module} data={profile} />
            ));
          })}
        </div>
      )}

      <LinkedInline
        modules={modules}
        configMap={{ ...ONLINE_SERVICES_CONFIG, ...EMAIL_PROFILE_CONFIG }}
        label="Онлайн-сервисы"
      />
    </div>
  );
};

const EmailOsintSection: React.FC<{
  lookup: OsintLookup | null | undefined;
  isEnriching: boolean;
  completedModules: number;
  totalModules: number;
}> = ({ lookup, isEnriching, completedModules, totalModules }) => {
  const modules = lookupToModules(lookup);
  const emailProfiles = modules.filter((m) => m.found && EMAIL_PROFILE_CONFIG[m.module]);
  const ai = lookup?.ai_analysis;
  const hasAi = Boolean(ai && (ai.profile_analyzer || ai.visual_analyzer));
  const hasAnyData = modules.length > 0 || hasAi;

  if (!isEnriching && !hasAnyData) return null;
  if (isEnriching && !hasAnyData) return <OsintProgressBar completed={completedModules} total={totalModules} />;

  return (
    <div className="space-y-0.5 mt-1">
      {isEnriching && completedModules < totalModules && (
        <OsintProgressBar completed={completedModules} total={totalModules} />
      )}

      {hasAi && (
        <div className="mt-1.5 mb-2">
          <AiAnalysisCard data={ai} />
        </div>
      )}

      <CallerIdNamesInline modules={modules} />
      <LinkedInline
        modules={modules}
        configMap={{ ...ONLINE_SERVICES_CONFIG, ...EMAIL_PROFILE_CONFIG }}
        label="Онлайн-сервисы"
      />

      {emailProfiles.length > 0 && (
        <div className="space-y-2 mt-2">
          {emailProfiles.flatMap((mod) => {
            const profiles = mod.result?.profiles || [mod.result];
            return profiles.map((profile: any, idx: number) => (
              <EmailProfileCard key={`${mod.module}-${idx}`} module={mod.module} data={profile} />
            ));
          })}
        </div>
      )}
    </div>
  );
};

// ============ Main Component ============

interface ContactInfoSectionProps {
  phonesValidation?: PhonesValidationResult | null;
  emailsValidation?: EmailsValidationResult | null;
  addressesValidation?: ArrayValidationResult | null;
  validationFields?: Record<string, ValidationFieldState>;
  className?: string;
  phonesOnly?: boolean;
  emailsOnly?: boolean;
  addressesOnly?: boolean;
}

const ContactInfoSection: React.FC<ContactInfoSectionProps> = ({
  phonesValidation,
  emailsValidation,
  addressesValidation,
  validationFields,
  className,
  phonesOnly = false,
  emailsOnly = false,
  addressesOnly = false,
}) => {
  const phones = React.useMemo(() => phonesValidation?.items || [], [phonesValidation?.items]);
  const emails = React.useMemo(() => emailsValidation?.items || [], [emailsValidation?.items]);
  const addresses = React.useMemo(() => addressesValidation?.items || [], [addressesValidation?.items]);

  const phonesValidating = validationFields?.phones?.status === "running";
  const emailsValidating = validationFields?.emails?.status === "running";
  const addressesValidating = validationFields?.addresses?.status === "running";

  // Render phones
  const renderPhones = () => {
    if (emailsOnly || addressesOnly) return null;
    if (phones.length === 0 && !phonesValidating) return null;

    return (
      <div className="border border-line rounded-md bg-surface-elevated overflow-hidden">
        {phones.map((phone, idx) => {
          const displayValue = phone.value_formatted_8 || phone.value || `Телефон ${idx + 1}`;
          const operator = phone.operator_brand || phone.provider;
          const region = phone.region;

          return (
            <div
              key={`phone-${idx}`}
              className={cn("px-3 py-2.5", idx < phones.length - 1 && "border-b border-dashed border-line")}
            >
              <div className="flex justify-between items-start">
                <span className="inline-flex items-center gap-1.5 text-sm font-normal text-ink-1">
                  {displayValue}
                  <OsintConfidenceIcon lookup={phone.osint_lookup} isEnriching={!!phone.osint_enriching} />
                </span>
              </div>
              {(operator || region) && (
                <div className="mt-1 space-y-0.5">
                  {operator && <div className="text-xs text-ink-3">— {operator}</div>}
                  {region && <div className="text-xs text-ink-3">— {region}</div>}
                </div>
              )}
              <PhoneOsintSection
                lookup={phone.osint_lookup}
                isEnriching={!!phone.osint_enriching}
                completedModules={phone.osint_completed_modules || 0}
                totalModules={phone.osint_total_modules || 0}
              />
            </div>
          );
        })}
        {phonesValidating && phones.length === 0 && (
          <div className="px-3 py-3 flex items-center gap-2 text-ink-3">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Валидация телефонов...</span>
          </div>
        )}
      </div>
    );
  };

  // Render emails
  const renderEmails = () => {
    if (phonesOnly || addressesOnly) return null;
    if (emails.length === 0 && !emailsValidating) return null;

    return (
      <div className="border border-line rounded-md bg-surface-elevated overflow-hidden">
        {emails.map((email, idx) => {
          const displayValue = email.value || `Email ${idx + 1}`;
          const domainType = email.domain_type;

          return (
            <div
              key={`email-${idx}`}
              className={cn("px-3 py-2.5", idx < emails.length - 1 && "border-b border-dashed border-line")}
            >
              <div className="flex justify-between items-start">
                <div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-normal text-ink-1">
                    {displayValue}
                    <OsintConfidenceIcon lookup={email.osint_lookup} isEnriching={!!email.osint_enriching} />
                  </span>
                  {domainType && typeof domainType === "string" && (
                    <div className="text-xs text-ink-3">— {domainType}</div>
                  )}
                </div>
              </div>
              <EmailOsintSection
                lookup={email.osint_lookup}
                isEnriching={!!email.osint_enriching}
                completedModules={email.osint_completed_modules || 0}
                totalModules={email.osint_total_modules || 0}
              />
            </div>
          );
        })}
        {emailsValidating && emails.length === 0 && (
          <div className="px-3 py-3 flex items-center gap-2 text-ink-3">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Валидация email...</span>
          </div>
        )}
      </div>
    );
  };

  // Render addresses
  const renderAddresses = () => {
    if (phonesOnly || emailsOnly) return null;
    if (addresses.length === 0 && !addressesValidating) return null;

    return (
      <div className="border border-line rounded-md bg-surface-elevated overflow-hidden">
        {addresses.map((address, idx) => {
          const displayValue = address.normalized_value || address.input_value || `Адрес ${idx + 1}`;
          const additionalData = address.additional_data;
          const normalizedAddress = additionalData?.normalized as string | undefined;
          const postalCode = additionalData?.postal_code as string | undefined;
          const region = (additionalData?.region as string) || (additionalData?.region_name as string);

          return (
            <div
              key={`address-${idx}`}
              className={cn("px-3 py-2.5", idx < addresses.length - 1 && "border-b border-dashed border-line")}
            >
              <span className="text-sm font-normal text-ink-1">{normalizedAddress || displayValue}</span>
              {(postalCode || region) && (
                <div className="mt-1 space-y-0.5">
                  {postalCode && <div className="text-xs text-ink-3">— {postalCode}</div>}
                  {region && <div className="text-xs text-ink-3">— {region}</div>}
                </div>
              )}
            </div>
          );
        })}
        {addressesValidating && addresses.length === 0 && (
          <div className="px-3 py-3 flex items-center gap-2 text-ink-3">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Валидация адресов...</span>
          </div>
        )}
      </div>
    );
  };

  const phoneContent = renderPhones();
  const emailContent = renderEmails();
  const addressContent = renderAddresses();

  if (!phoneContent && !emailContent && !addressContent) return null;

  if (phonesOnly) return <div className={className}>{phoneContent}</div>;
  if (emailsOnly) return <div className={className}>{emailContent}</div>;
  if (addressesOnly) return <div className={className}>{addressContent}</div>;

  return (
    <div className={cn("space-y-4", className)}>
      {phoneContent}
      {emailContent}
      {addressContent}
    </div>
  );
};

export { ContactInfoSection };
